// SPDX-License-Identifier: MIT
/**
 * QA probe (§7.10). In QA sessions the runtime instruments its drawing contexts so that every
 * `fillText` / `strokeText` is recorded with its on-screen box, effective alpha and whether a clip
 * was active. Scenes need no cooperation: the checker sees exactly the text that was drawn.
 * Render sessions never create a probe, so normal frames pay nothing.
 */
import { DESIGN_H, DESIGN_W, type QAReporter } from './scene.ts';
type Ctx = CanvasRenderingContext2D;
type Box = [
    x: number,
    y: number,
    w: number,
    h: number
];
export type TextRun = {
    /** Scene entry id that drew the text. */
    scene: string;
    text: string;
    /** On-screen bounding box in design units (1920 × 1080). */
    box: Box;
    /** Baseline anchor in design units, used to group glyph runs into lines. */
    anchor: [
        number,
        number
    ];
    /** Font size in design units after transforms. */
    size: number;
    /** globalAlpha × colour alpha (transition alpha included). */
    alpha: number;
    /** Drawn while a clip was active (masked reveals): its visible part may be smaller than its box. */
    clipped: boolean;
    /** Drawn into an offscreen layer (glows, mattes) rather than the frame itself. */
    layer: boolean;
};
export type ElementReport = {
    scene: string;
    id: string;
    box: Box;
};
export type SceneTextReport = {
    scene: string;
    id: string;
    box: Box;
    alpha: number;
    text?: string;
};
export type FrameProbe = {
    runs: TextRun[];
    elements: ElementReport[];
    reports: SceneTextReport[];
};
const PATCHED = new WeakSet<object>();
let active: TextProbe | null = null;
/** Called by kit layers so that text drawn into offscreen layers is captured too. */
export function instrumentLayer(ctx: Ctx): void {
    active?.instrument(ctx, true);
}
function colorAlpha(style: unknown): number {
    if (typeof style !== 'string')
        return 1;
    const m = /^rgba?\(([^)]+)\)$/.exec(style.trim());
    if (m) {
        const parts = m[1]!.split(/[\s,/]+/).filter(Boolean);
        if (parts.length === 4) {
            const a = parts[3]!;
            return a.endsWith('%') ? parseFloat(a) / 100 : parseFloat(a);
        }
        return 1;
    }
    const hex = /^#([0-9a-f]{8})$/i.exec(style.trim());
    return hex ? parseInt(hex[1]!.slice(6), 16) / 255 : 1;
}
export class TextProbe {
    private frame: FrameProbe = { runs: [], elements: [], reports: [] };
    private scene = '';
    /** Per-context stack of "clip active" flags mirroring save()/restore(). */
    private clips = new WeakMap<object, boolean[]>();
    private readonly toDesign: [
        number,
        number
    ];
    constructor(size: [
        number,
        number
    ]) {
        this.toDesign = [DESIGN_W / size[0], DESIGN_H / size[1]];
    }
    begin(): void {
        this.frame = { runs: [], elements: [], reports: [] };
        // Layers are created inside scene code and only reach the probe through this module slot.
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        active = this;
    }
    end(): FrameProbe {
        active = null;
        return this.frame;
    }
    /** Attribute subsequent draws to a scene entry. */
    enterScene(id: string): void {
        this.scene = id;
    }
    /** Scene-scoped reporter for `env.report` (optional hints next to the automatic capture). */
    reporter(ctx: Ctx, scene: string): QAReporter {
        const map = (box: Box) => this.map(ctx, box);
        return {
            text: (id, box, alpha, text) => this.frame.reports.push({ scene, id, box: map(box), alpha, ...(text !== undefined ? { text } : {}) }),
            element: (id, box) => this.frame.elements.push({ scene, id, box: map(box) }),
        };
    }
    /** Wrap the text and clip methods of `ctx` once; records only while this probe is active. */
    instrument(ctx: Ctx, layer = false): void {
        if (PATCHED.has(ctx))
            return;
        PATCHED.add(ctx);
        const clips = () => {
            let s = this.clips.get(ctx);
            if (!s)
                this.clips.set(ctx, (s = [false]));
            return s;
        };
        const wrap = <K extends 'save' | 'restore' | 'clip' | 'reset'>(name: K, before: () => void) => {
            const orig = ctx[name].bind(ctx) as (...args: unknown[]) => unknown;
            (ctx as unknown as Record<string, unknown>)[name] = (...args: unknown[]) => {
                before();
                return orig(...args);
            };
        };
        wrap('save', () => {
            const s = clips();
            s.push(s[s.length - 1]!);
        });
        wrap('restore', () => {
            const s = clips();
            if (s.length > 1)
                s.pop();
        });
        wrap('clip', () => {
            const s = clips();
            s[s.length - 1] = true;
        });
        wrap('reset', () => this.clips.set(ctx, [false]));
        for (const name of ['fillText', 'strokeText'] as const) {
            const orig = ctx[name].bind(ctx);
            ctx[name] = (text: string, x: number, y: number, maxWidth?: number) => {
                if (active === this)
                    this.record(ctx, name === 'fillText', String(text), x, y, layer, clips());
                if (maxWidth === undefined)
                    orig(text, x, y);
                else
                    orig(text, x, y, maxWidth);
            };
        }
    }
    private map(ctx: Ctx, [x, y, w, h]: Box): Box {
        const m = ctx.getTransform();
        const xs: number[] = [];
        const ys: number[] = [];
        for (const [px, py] of [
            [x, y],
            [x + w, y],
            [x, y + h],
            [x + w, y + h],
        ] as const) {
            xs.push((m.a * px + m.c * py + m.e) * this.toDesign[0]);
            ys.push((m.b * px + m.d * py + m.f) * this.toDesign[1]);
        }
        const x0 = Math.min(...xs);
        const y0 = Math.min(...ys);
        return [x0, y0, Math.max(...xs) - x0, Math.max(...ys) - y0];
    }
    private record(ctx: Ctx, fill: boolean, text: string, x: number, y: number, layer: boolean, clip: boolean[]) {
        if (!text)
            return;
        const alpha = ctx.globalAlpha * colorAlpha(fill ? ctx.fillStyle : ctx.strokeStyle);
        if (alpha <= 0.01)
            return;
        const mt = ctx.measureText(text);
        // Spaces have no ink; keep them as zero-height runs so per-glyph text keeps its word breaks.
        const box = text.trim()
            ? this.map(ctx, [
                x - mt.actualBoundingBoxLeft,
                y - mt.actualBoundingBoxAscent,
                mt.actualBoundingBoxLeft + mt.actualBoundingBoxRight,
                mt.actualBoundingBoxAscent + mt.actualBoundingBoxDescent,
            ])
            : this.map(ctx, [
                ctx.textAlign === 'center'
                    ? x - mt.width / 2
                    : ctx.textAlign === 'right' || ctx.textAlign === 'end'
                        ? x - mt.width
                        : x,
                y,
                mt.width,
                0,
            ]);
        const m = ctx.getTransform();
        const scale = Math.sqrt(Math.abs(m.a * m.d - m.b * m.c)) * this.toDesign[0];
        const px = parseFloat(/(\d+(?:\.\d+)?)px/.exec(ctx.font)?.[1] ?? '0');
        this.frame.runs.push({
            scene: this.scene,
            text,
            box,
            anchor: [(m.a * x + m.c * y + m.e) * this.toDesign[0], (m.b * x + m.d * y + m.f) * this.toDesign[1]],
            size: px * scale,
            alpha,
            clipped: clip[clip.length - 1] ?? false,
            layer,
        });
    }
}
export type LumaStats = {
    /** Mean relative luminance (linear light, 0–1). */
    mean: number;
    /** Share of pixels that are near white (display value > 0.92 on every channel). */
    white: number;
    /** Mean relative luminance per cell, row-major from the top-left. */
    grid: number[];
    cols: number;
    rows: number;
};
const SRGB_TO_LINEAR = Array.from({ length: 256 }, (_, i) => {
    const c = i / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
});
/** Luminance statistics of an RGBA frame whose rows are stored bottom-up (WebGL readPixels order). */
export function lumaStats(rgba: Uint8Array, width: number, height: number, cols = 4, rows = 3): LumaStats {
    const sums = new Float64Array(cols * rows);
    const counts = new Uint32Array(cols * rows);
    let total = 0;
    let white = 0;
    const step = Math.max(1, Math.floor(Math.min(width, height) / 180));
    let n = 0;
    for (let y = 0; y < height; y += step) {
        const row = Math.min(rows - 1, Math.floor(((height - 1 - y) / height) * rows));
        for (let x = 0; x < width; x += step) {
            const i = (y * width + x) * 4;
            const r = rgba[i]!;
            const g = rgba[i + 1]!;
            const b = rgba[i + 2]!;
            const l = 0.2126 * SRGB_TO_LINEAR[r]! + 0.7152 * SRGB_TO_LINEAR[g]! + 0.0722 * SRGB_TO_LINEAR[b]!;
            const cell = row * cols + Math.min(cols - 1, Math.floor((x / width) * cols));
            sums[cell]! += l;
            counts[cell]!++;
            total += l;
            if (r > 235 && g > 235 && b > 235)
                white++;
            n++;
        }
    }
    return {
        mean: total / n,
        white: white / n,
        grid: Array.from(sums, (s, i) => s / Math.max(1, counts[i]!)),
        cols,
        rows,
    };
}
/** FNV-1a over the frame bytes, for determinism checks. */
export function frameHash(bytes: Uint8Array): string {
    let h = 0x811c9dc5;
    for (let i = 0; i < bytes.length; i++) {
        h ^= bytes[i]!;
        h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16).padStart(8, '0');
}
